import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrganizations,
  listActivePlans,
  getOrgSubscription,
  updateOrgPlan,
  addOrgCredits,
  addOrgCreditBalance,
  type AdminOrg,
  type SubscriptionPlan,
  type SubscriptionWithUsage,
} from '../api/admin';

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const PLAN_COLORS = [
  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
];

/* ------------------------------------------------------------------ */
/*  Per-org subscription detail                                        */
/* ------------------------------------------------------------------ */

function OrgSubscriptionCard({ org, plans }: { org: AdminOrg; plans: SubscriptionPlan[] }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [bonusTokens, setBonusTokens] = useState(0);
  const [bonusRequests, setBonusRequests] = useState(0);
  const [bonusCredits, setBonusCredits] = useState(0);
  const [creditReason, setCreditReason] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const subQuery = useQuery<SubscriptionWithUsage>({
    queryKey: ['admin', 'subscription', org.id],
    queryFn: () => getOrgSubscription(org.id),
    enabled: expanded,
  });

  const planMutation = useMutation({
    mutationFn: (planId: string) => updateOrgPlan(org.id, planId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscription', org.id] });
      setSuccessMsg('Plan updated');
      setTimeout(() => setSuccessMsg(''), 2500);
    },
  });

  const creditsMutation = useMutation({
    mutationFn: () =>
      addOrgCredits(org.id, {
        bonus_tokens: bonusTokens,
        bonus_requests: bonusRequests,
        reason: creditReason || undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscription', org.id] });
      setSuccessMsg(res.message);
      setBonusTokens(0);
      setBonusRequests(0);
      setCreditReason('');
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const creditBalanceMutation = useMutation({
    mutationFn: () =>
      addOrgCreditBalance(org.id, {
        bonus_credits: bonusCredits,
        reason: creditReason || undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscription', org.id] });
      setSuccessMsg(res.message);
      setBonusCredits(0);
      setCreditReason('');
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const sub = subQuery.data?.subscription;
  const usage = subQuery.data?.usage;
  const limits = subQuery.data?.effective_limits;
  const currentPlanSlug = sub?.plan ?? 'starter';

  const currentPlan = plans.find((p) => p.id === sub?.plan_id || p.slug === currentPlanSlug);
  const planIndex = currentPlan ? plans.indexOf(currentPlan) : 0;
  const ps = PLAN_COLORS[planIndex % PLAN_COLORS.length];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Summary row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{org.name}</div>
          <div className="text-xs text-slate-500 truncate">{org.email || '—'}</div>
        </div>

        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${ps.bg} ${ps.text} ${ps.border}`}>
          {currentPlan?.name || currentPlanSlug}
        </span>

        {sub?.byok_enabled && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200">
            BYOK
          </span>
        )}

        <div className="text-xs text-slate-500">
          {org.site_count} site{org.site_count !== 1 ? 's' : ''} &middot; {org.user_count} user{org.user_count !== 1 ? 's' : ''}
        </div>

        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-100 px-6 py-5 bg-slate-50 space-y-5">
          {subQuery.isLoading && (
            <div className="text-center py-6 text-slate-400 text-sm">Loading subscription...</div>
          )}

          {subQuery.isError && (
            <div className="text-center py-6 text-red-500 text-sm">Failed to load subscription data.</div>
          )}

          {sub && usage && limits && (
            <>
              {successMsg && (
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700">
                  {successMsg}
                </div>
              )}

              {/* Plan & Fee summary */}
              {currentPlan && (
                <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Current Plan</div>
                    <div className="text-lg font-bold text-slate-900">{currentPlan.name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Subscription Fee</div>
                    <div className="text-lg font-bold text-slate-900">
                      {currentPlan.price_cents === 0 ? 'Free' : `$${(currentPlan.price_cents / 100).toFixed(0)}`}
                      {currentPlan.price_cents > 0 && <span className="text-xs text-slate-500 ml-1">/{currentPlan.billing_interval === 'yearly' ? 'yr' : 'mo'}</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Monthly Credits</div>
                    <div className="text-lg font-bold text-purple-700">{fmtNum(currentPlan.monthly_credits)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">BYOK</div>
                    <div className={`text-sm font-bold ${sub.byok_enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {sub.byok_enabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Days Remaining</div>
                    <div className="text-lg font-bold text-slate-900">{usage.days_remaining}</div>
                  </div>
                </div>
              )}

              {/* Credit usage with progress bar */}
              <div className="bg-white rounded-lg border border-purple-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-purple-700">Credit Usage</div>
                  <div className="text-xs text-slate-500">
                    {fmtNum(usage.credits_used)} / {fmtNum(limits.total_credit_limit)} ({limits.credit_usage_percent}%)
                  </div>
                </div>
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      limits.credit_usage_percent >= 90 ? 'bg-red-500' :
                      limits.credit_usage_percent >= 75 ? 'bg-amber-500' : 'bg-purple-600'
                    }`}
                    style={{ width: `${Math.min(limits.credit_usage_percent, 100)}%` }}
                  />
                </div>
                {sub.bonus_credits > 0 && (
                  <div className="text-[10px] text-purple-500 mt-1">+{fmtNum(sub.bonus_credits)} bonus credits included</div>
                )}
              </div>

              {/* Usage stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatBox label="Credits used" value={fmtNum(usage.credits_used)} sub={`/ ${fmtNum(limits.total_credit_limit)}`} />
                <StatBox label="Credits remaining" value={fmtNum(usage.credits_remaining)} />
                <StatBox label="Events used" value={fmtNum(usage.events_used)} sub={`/ ${fmtNum(limits.total_event_limit)} (${limits.event_usage_percent}%)`} />
                <StatBox label="Sessions used" value={fmtNum(usage.sessions_used)} sub={`/ ${fmtNum(limits.total_session_limit)} (${limits.session_usage_percent}%)`} />
                <StatBox label="Tokens used" value={fmtNum(usage.tokens_used)} sub={`/ ${fmtNum(limits.total_token_limit)}`} />
                <StatBox label="Sites / Seats" value={`${usage.sites_used} / ${limits.max_seats === -1 ? '∞' : limits.max_seats}`} sub={`max ${limits.max_sites === -1 ? '∞' : limits.max_sites} sites`} />
              </div>

              {/* Change plan */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-xs font-semibold text-slate-700 mb-3">Change Plan</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => planMutation.mutate(plan.id)}
                      disabled={planMutation.isPending || sub.plan_id === plan.id}
                      className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                        sub.plan_id === plan.id || (!sub.plan_id && sub.plan === plan.slug)
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {plan.name}
                    </button>
                  ))}
                  {planMutation.isError && (
                    <span className="text-xs text-red-600 ml-2">Failed to update plan</span>
                  )}
                </div>
              </div>

              {/* Add unified credits */}
              <div className="bg-white rounded-lg border border-purple-200 p-4">
                <div className="text-xs font-semibold text-purple-700 mb-3">Add Unified Credits</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Bonus credits</div>
                    <input
                      type="number"
                      min={0}
                      value={bonusCredits}
                      onChange={(e) => setBonusCredits(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Reason (optional)</div>
                    <input
                      type="text"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      placeholder="e.g. support request"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </label>
                </div>
                <button
                  onClick={() => creditBalanceMutation.mutate()}
                  disabled={creditBalanceMutation.isPending || bonusCredits === 0}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {creditBalanceMutation.isPending ? 'Adding...' : 'Add Credits'}
                </button>
                {creditBalanceMutation.isError && (
                  <span className="text-xs text-red-600 ml-3">Failed to add credits</span>
                )}
              </div>

              {/* Add legacy tokens (backward compat) */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-xs font-semibold text-slate-700 mb-3">Add Legacy Tokens</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Bonus tokens</div>
                    <input
                      type="number"
                      min={0}
                      value={bonusTokens}
                      onChange={(e) => setBonusTokens(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Bonus requests</div>
                    <input
                      type="number"
                      min={0}
                      value={bonusRequests}
                      onChange={(e) => setBonusRequests(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Reason (optional)</div>
                    <input
                      type="text"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      placeholder="e.g. support request"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </label>
                </div>
                <button
                  onClick={() => creditsMutation.mutate()}
                  disabled={creditsMutation.isPending || (bonusTokens === 0 && bonusRequests === 0)}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50 transition-colors"
                >
                  {creditsMutation.isPending ? 'Adding...' : 'Add Tokens'}
                </button>
                {creditsMutation.isError && (
                  <span className="text-xs text-red-600 ml-3">Failed to add credits</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

export function SubscriptionsPage() {
  const [filter, setFilter] = useState('');

  const orgsQuery = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: listOrganizations,
  });

  const plansQuery = useQuery({
    queryKey: ['admin', 'active-plans'],
    queryFn: listActivePlans,
  });

  const plans = plansQuery.data || [];

  const orgs = (orgsQuery.data || []).filter(
    (o: AdminOrg) =>
      !filter ||
      o.name.toLowerCase().includes(filter.toLowerCase()) ||
      (o.email && o.email.toLowerCase().includes(filter.toLowerCase())),
  );

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Subscriptions</h1>
        <p className="text-slate-600 mt-2">
          Manage plans, usage limits, and credits for each organization.
        </p>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search organizations..."
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div className="text-sm text-slate-600">
          {orgs.length} org{orgs.length !== 1 ? 's' : ''}
        </div>
      </div>

      {(orgsQuery.isLoading || plansQuery.isLoading) && (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      )}

      {!orgsQuery.isLoading && !plansQuery.isLoading && orgs.length === 0 && (
        <div className="text-center py-12 text-slate-400">No organizations found</div>
      )}

      {!orgsQuery.isLoading && !plansQuery.isLoading && orgs.length > 0 && (
        <div className="space-y-3">
          {orgs.map((org: AdminOrg) => (
            <OrgSubscriptionCard key={org.id} org={org} plans={plans} />
          ))}
        </div>
      )}
    </div>
  );
}
